import { Box, BoxSx } from "@modernatx-internal/component-library";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";

import { ContentComponentProps, ImageContent } from "../types";

export const PageImageWithTransition: React.FC<ContentComponentProps<ImageContent>> = (props) => {
  const {
    alt = "",
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    children,
    src: srcProp,
    sx: sxProp,
    transition = "fade-in",
    ...rest
  } = props;
  const idGenerated = `Image${React.useId().replace(/[^A-Z0-9]/gi, "")}`;
  const id = props.id || idGenerated;
  const { screenshot } = useExperience();
  const image = React.useRef<HTMLImageElement | null>(null);
  const transitioning = React.useRef(false);
  const sx = React.useRef<BoxSx | undefined>(sxProp);
  const [currentSrc, currentSrcSet] = React.useState(srcProp);
  const [loaded, loadedSet] = React.useState(false);
  const [visible, visibleSet] = React.useState(false);

  // On the initial image load, just check to see when it is loaded
  // The "onLoad" callback doesn't work (for some reason), so we use a ref
  React.useEffect(() => {
    if (image.current && srcProp === currentSrc && visible) {
      if (image.current.complete && image.current.naturalWidth !== 0) {
        loadedSet(true);
      } else {
        image.current.onload = () => {
          loadedSet(true);
        };

        image.current.onerror = () => {
          loadedSet(true);
        };
      }
    }
  }, [srcProp, currentSrc, visible]);

  // Images won't fire load events if they are not displayed,
  // so we need to set a flag for when they are so we can check
  // their download state
  React.useEffect(() => {
    if (!image.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        visibleSet(!!entry?.isIntersecting);
      },
      {
        root: null,
        threshold: 0
      }
    );

    observer.observe(image.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleTransitionEnd = React.useCallback(
    (e: React.TransitionEvent<HTMLImageElement>) => {
      if (e.currentTarget === e.target) {
        transitioning.current = false;
        currentSrcSet(srcProp);
      }
    },
    [srcProp]
  );

  if (srcProp !== currentSrc) {
    if (visible && !transitioning.current) {
      if (transition === "none") {
        currentSrcSet(srcProp);
      } else {
        transitioning.current = true;
        loadedSet(false);
      }
    } else if (!visible) {
      currentSrcSet(srcProp);
    }
  } else {
    sx.current = sxProp;
  }

  return (
    <>
      <Box
        as="img"
        {...rest}
        ref={image}
        id={id}
        alt={alt}
        src={currentSrc}
        sx={{
          opacity: screenshot || loaded ? 1 : 0,
          transition: "transition-color-medium",
          willChange: "opacity",
          ...sx.current
        }}
        onTransitionEnd={handleTransitionEnd}
      />
      {/* Display the block if JavaScript is disabled. */}
      <noscript>
        <style>{`#${id} { opacity: 1 !important; }`}</style>
      </noscript>
    </>
  );
};

export const PageImage: React.FC<ContentComponentProps<ImageContent>> = (props) => {
  const { transition = "fade-in" } = props;

  if (transition === "fade-in" || transition === "none") {
    return [<PageImageWithTransition key={props.src} {...props} />];
  }

  return <PageImageWithTransition {...props} />;
};
